import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import '@adornis/forms/x-input.js';
import '@adornis/forms/x-textarea.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as m from '../../../../localization/out/messages.js';
import { getCompanyByID } from '../../../api/company.js';
import { CompanyLogoFile } from '../../../db/CompanyLogoFile.js';
import { Company, SocialNetwork } from '../../../db/company.js';
import { formatFormError } from '../../components/format-form-error.js';
import { labeledField } from '../../components/labeled-field.js';
import { MultilanguageFormattableTextarea } from '../../components/multilanguage-formattable-textarea.js';
import { mainRouter } from '../../mainRouter.js';

export const CompanyProfile = stateful(({ companyID }: { companyID: string }) => {
  const company = useQuery(async () => {
    if (!companyID) return null;
    return (await getCompanyByID(companyID)({
      _id: 1,
      name: 1,
      address: 1,
      sector: 1,
      services: 1,
      logoID: 1,
      cultureAndValues: selectionSet(() => TextMultipleLangItem, 1),
      benefits: selectionSet(() => TextMultipleLangItem, 1),
      website: 1,
      socialNetworks: { _id: 1, socialNetwork: 1, link: 1 },
      userIDs: 1,
    })) as Company | null;
  }, [companyID]);

  if (company.loading)
    return html`
      <div class="p-6">
        <div class="hero min-h-[60vh] bg-base-200 rounded-box">
          <div class="hero-content text-center">
            <div class="flex flex-col items-center">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <p class="mt-4 opacity-70">${m.company_profile_loading()}</p>
            </div>
          </div>
        </div>
      </div>
    `;
  if (!company.value)
    return html`
      <div class="p-6">
        <div class="hero min-h-[60vh] bg-base-200 rounded-box">
          <div class="hero-content text-center">
            <div class="max-w-md">
              <div class="mb-3 flex justify-center">
                <div class="bg-base-300 text-base-content w-16 h-16 rounded-full flex items-center justify-center">
                  <span class="icon-[material-symbols--apartment] text-3xl"></span>
                </div>
              </div>
              <h2 class="text-2xl font-bold mb-2">${m.company_profile_title()}</h2>
              <p class="opacity-70 mb-4">${m.company_profile_not_found()}</p>
              <div class="card-actions justify-center">
                <button class="btn btn-primary" @click=${() => location.reload()}>${m.company_profile_reload()}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  return CompanyProfileForm({ company: company.value });
});

const CompanyProfileForm = stateful(({ company }: { company: Company }) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<null | string>(null);
  // Used to force a re-render after mutating non-reactive nested arrays
  // (form.document.socialNetworks push/splice don't trigger lit reactivity).
  const [socialNetworksRev, setSocialNetworksRev] = useState(0);
  const bumpSocialNetworks = () => setSocialNetworksRev(socialNetworksRev + 1);
  const { form, renderField } = useGeneratedForm<Company>(
    company,
    { translationPrefix: 'company_profile_', validator: null },
    [company],
  );

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const validationError = await form.validate();
      if (validationError) {
        setSaveError(formatFormError(validationError, 'company_profile_'));
        return;
      }
      // Upload any files picked via FileInput before saving the entity
      // so the resulting file IDs land on the document (e.g. logoID).
      await form.uploadAllPendingFiles();
      await company.save();
      XSnackbar.show(m.company_profile_saved());
      // CompanyProfile is mounted under both /admin/company/<id>/edit and /company/profile.
      // mainRouter.goTo('company') unconditionally drops admins into MwCompanyDashboard, where
      // they have no associated company and hit "Kein Unternehmen zugeordnet".
      if (window.location.pathname.startsWith('/admin/')) {
        goTo({ path: `/admin/company/${company._id}` });
      } else {
        mainRouter.goTo('company');
      }
    } catch (e: any) {
      console.error(e);
      setSaveError(formatFormError(e, 'company_profile_'));
    } finally {
      setSaving(false);
    }
  };

  return html`
      <div class="max-w-4xl w-full">
        <div class="space-y-10">

          ${
            saveError
              ? html`<div class="alert alert-error">
                  <span class="icon-[material-symbols--error] text-error"></span>
                  <span>${saveError}</span>
                </div>`
              : null
          }
          <div class="card bg-base-100 shadow-xl">
            <div class="card-body">
              <h2 class="card-title text-2xl mb-4">${m.company_profile_identity()}</h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                ${labeledField(
                  m.company_profile_logoID_label(),
                  renderField(['logoID'], props =>
                    FileInput({
                      ...props,
                      fileEntity: CompanyLogoFile,
                      accept: 'image/jpeg,image/png,image/gif,image/webp',
                      placeholder: m.company_profile_logo_placeholder(),
                    }),
                  ),
                )}
                ${labeledField(m.company_profile_name_label(), renderField(['name']))}
                ${labeledField(m.company_profile_address_label(), renderField(['address']))}
                ${labeledField(m.company_profile_sector_label(), renderField(['sector']))}
                <div class="col-span-2">
                  ${labeledField(m.company_profile_services_label(), renderField(['services'], TextareaInput))}
                </div>
                <div class="col-span-2">
                  ${labeledField(m.company_profile_website_label(), renderField(['website']))}
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-100 shadow-xl">
            <div class="card-body">
              <h2 class="card-title text-2xl mb-4">${m.company_profile_culture_title()}</h2>
              ${renderField(['cultureAndValues'], MultilanguageFormattableTextarea)}
            </div>
          </div>

          <div class="card bg-base-100 shadow-xl">
            <div class="card-body">
              <h2 class="card-title text-2xl mb-4">${m.company_profile_benefits_title()}</h2>
              ${renderField(['benefits'], MultilanguageFormattableTextarea)}
            </div>
          </div>

          <div class="card bg-base-100 shadow-xl">
            <div class="card-body">
              <h2 class="card-title text-2xl mb-4">${m.company_profile_social_media_title()}</h2>
              <div class="space-y-4">
                ${repeat(
                  form.document?.socialNetworks ?? [],
                  (socialNetwork, i) => html`
                    <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                      ${labeledField(
                        m.company_profile_socialNetworks_socialNetwork_label(),
                        renderField(['socialNetworks', i, 'socialNetwork']),
                      )}
                      ${labeledField(
                        m.company_profile_socialNetworks_link_label(),
                        renderField(['socialNetworks', i, 'link']),
                      )}
                      <button
                        type="button"
                        class="btn btn-ghost btn-square text-error"
                        @click=${() => {
                          form.document?.socialNetworks?.splice(i, 1);
                          bumpSocialNetworks();
                        }}
                      >
                        <span class="icon-[material-symbols--delete]"></span>
                      </button>
                    </div>
                  `,
                )}
              </div>
              <div class="card-actions justify-end mt-4">
                <button
                  type="button"
                  class="btn btn-outline btn-primary"
                  @click=${() => {
                    if (form.document) {
                      if (!form.document.socialNetworks) form.document.socialNetworks = [];
                      form.document.socialNetworks.push(new SocialNetwork({}));
                      bumpSocialNetworks();
                    }
                  }}
                >
                  <span class="icon-[material-symbols--add]"></span>
                  ${m.company_profile_add_social_media_button()}
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-4 mt-8">
            <button
              @click=${handleSave}
              class="btn btn-primary"
              ?disabled=${saving}
            >
              ${saving ? html`<span class="loading loading-spinner"></span>` : null}
              <span>${m.company_profile_save_button()}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
});
